import { combineReducers, UnknownAction } from 'redux';
import { configureStore } from '@reduxjs/toolkit';
import { createLogger } from 'redux-logger';
import ordersReducer, { initialState as ordersInitial, OrdersState } from 'store/orders/ordersSlice';
import authReducer, { AuthState, initialState as authInitial, logout } from './auth/authSlice';
import basketReducer, { BasketState, initialState as basketInitial } from './basket/basketSlice';
import { basketListenerMiddleware } from './basket/basketListenerMiddleware';
import guestReducer, { GuestState, initialState as guestInitial } from './guest/guestSlice';
import layoutReducer, { initialState as layoutInitial, LayoutState } from './layout/layoutSlice';
import locationsReducer, {
    initialState as locationInitial,
    LocationsState
} from './locations/locationsSlice';
import menuReducer, { initialState as menuInitial, MenuState } from './menu/menuSlice';
import requestReducer, { initialState as requestInitial, RequestState } from './request/requestSlice';
import settingsReducer, { initialState as settingsInitial, SettingsState } from './settings/settingsSlice';

export interface ApplicationState {
    auth: AuthState;
    locations: LocationsState;
    settings: SettingsState;
    layout: LayoutState;
    menu: MenuState;
    basket: BasketState;
    guest: GuestState;
    request: RequestState;
    orders: OrdersState;
}

const appReducer = combineReducers({
    auth: authReducer,
    locations: locationsReducer,
    settings: settingsReducer,
    layout: layoutReducer,
    menu: menuReducer,
    basket: basketReducer,
    guest: guestReducer,
    request: requestReducer,
    orders: ordersReducer
});

const emptyState: ApplicationState = {
    auth: authInitial,
    locations: locationInitial,
    settings: settingsInitial,
    layout: layoutInitial,
    menu: menuInitial,
    basket: basketInitial,
    guest: guestInitial,
    request: requestInitial,
    orders: ordersInitial
};

const rootReducer = (state: ApplicationState | undefined, action: UnknownAction): ApplicationState => {
    // Reset all state except auth on logout
    if (action.type === logout.fulfilled.type && !!state) {
        state = { ...emptyState, auth: state.auth };
    }

    return appReducer(state, action);
};

// Init store
export const initStore = (preloadedState = emptyState) => {
    const additionalMiddleware: any[] = [];
    // Client side logger
    if (process.env.NODE_ENV !== 'production') {
        additionalMiddleware.push(createLogger());
    }
    return configureStore({
        reducer: rootReducer,
        preloadedState,
        middleware: getDefaultMiddleware =>
            getDefaultMiddleware({
                // if we want to preserve state on page refresh, we will need to remove this part
                serializableCheck: {
                    ignoreActions: true,
                    ignoreState: true
                }
            })
                .prepend(basketListenerMiddleware.middleware)
                .concat(additionalMiddleware)
    });
};

export type AppStore = ReturnType<typeof initStore>;
export type AppDispatch = AppStore['dispatch'];
